<template>
  <v-data-table
    :headers="headers"
    :items="items"
    item-key="rank"
    hide-default-footer
    hide-default-body
    disable-pagination
    disable-sort
    hover
    class="pa-2"
  >
    <template #header.icon>
      <v-icon>mdi-drag</v-icon>
    </template>
    <template #tbody>
      <draggable
        v-model="items"
        tag="tbody"
        :key="anyEditing"
        :disabled="anyEditing"
        @end="updateRanks"
      >
        <tr v-for="(item, index) in items" :key="item.id">
          <td width="20px">
            <v-icon small :disabled="anyEditing">mdi-arrow-all</v-icon>
          </td>
          <td width="20px">{{ index + 1 }}</td>
          <td>
            <v-text-field
              v-if="isEditing(item)"
              v-model="item.name"
              :rules="[required]"
              :placeholder="'Name'"
              :autofocus="true"
              variant="underlined"
              density="compact"
            />
            <span v-else>{{ item.name }}</span>
          </td>
          <td>
            <v-text-field
              v-if="isEditing(item)"
              v-model="item.url"
              :placeholder="'Url'"
              variant="underlined"
              dense
              density="compact" />
            <span v-else><a :href="item.url" target="_blank">{{ item.url }}</a></span>
          </td>
          <td>
            <v-text-field
              v-if="isEditing(item)"
              v-model="item.price"
              :placeholder="'Price'"
              :rules="[isIntiger]"
              variant="underlined"
              dense
              density="compact" />
            <span v-else>{{ item.price }}</span>
          </td>
          <td width="20px">
            <v-icon v-if="!isEditing(item)" @click="toggleEditRow(item)" color="green">
              mdi-pencil
            </v-icon>
            <v-icon v-if="!isEditing(item)" @click="deleteItem(item.id)" color="blue">
              mdi-delete
            </v-icon>
            <v-icon v-if="isEditing(item)" @click="cancelEdit(item)" color="red">
              mdi-close
            </v-icon>
            <v-icon v-if="isEditing(item)" @click="saveRow(item)" color="green" :disabled="!isRowValid(item)">
              mdi-floppy
            </v-icon>
          </td>
        </tr>
      </draggable>
    </template>
  </v-data-table>
</template>

<script setup>
import { computed, ref } from 'vue';
import { VueDraggableNext as draggable } from 'vue-draggable-next';

const items = defineModel('items');

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});
function updateRanks() {
  items.value = items.value.map((item, index) => ({
    ...item,
    rank: index + 1,
  }));
}

const editingRowMap = ref({});
function isEditing(item) {
  return editingRowMap.value[item.id] === true;
}

const originalRows = ref({});

function toggleEditRow(item) {
  if (!editingRowMap.value[item.id]) {
    originalRows.value[item.id] = { ...item };
  }
  editingRowMap.value[item.id] = !editingRowMap.value[item.id];
}

function cancelEdit(item) {
  Object.assign(item, originalRows.value[item.id]);
  editingRowMap.value[item.id] = false;
}

function saveRow(item) {
  editingRowMap.value[item.id] = false;
}

function deleteItem(id) {
  const index = items.value.findIndex((i) => i.id === id);
  if (index === -1) return;
  items.value.splice(index, 1);
  delete editingRowMap.value[id];
  delete originalRows.value[id];
}

const isIntiger = (value) => !value || /^\d+$/.test(String(value)) || 'Must be an integer';
const required = (value) => !!value || 'Required';

function isRowValid(item) {
  const nameValid = required(item.name) === true;
  const priceValid = isIntiger(item.price) === true;
  return nameValid && priceValid;
}

const anyEditing = computed(() => Object.values(editingRowMap.value).some((v) => v));

defineExpose({
  anyEditing,
});
</script>

<style scoped>
::v-deep(.v-text-field input) {
  font-size: 14px;
}
</style>
